import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Settings, Globe, Languages, MessageSquare, Star } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import CountdownTimer from '@/components/CountdownTimer';
import EventCard from '@/components/EventCard';
import VoteBox from '@/components/VoteBox';
import { useLanguage } from '@/context/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIMEZONES = [
  { id: 'UTC', label: 'UTC', offset: 0 },
  { id: 'GMT', label: 'GMT', offset: 0 },
  { id: 'CET', label: 'CET (Berlin)', offset: 1 },
  { id: 'PST', label: 'PST (Los Angeles)', offset: -8 },
  { id: 'PDT', label: 'PDT (Los Angeles DST)', offset: -7 },
  { id: 'EST', label: 'EST (New York)', offset: -5 },
  { id: 'EDT', label: 'EDT (New York DST)', offset: -4 },
];

const LANGUAGES = [
  { id: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { id: 'en', label: 'English', flag: '🇬🇧' },
];

const HomePage = () => {
  const { language, setLanguage, t, months } = useLanguage();
  const [events, setEvents] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTimezone, setSelectedTimezone] = useState(() => {
    return localStorage.getItem('selectedTimezone') || 'UTC';
  });

  const currentTimezone = TIMEZONES.find(tz => tz.id === selectedTimezone) || TIMEZONES[0];
  const currentLanguage = LANGUAGES.find(l => l.id === language) || LANGUAGES[0];

  const handleTimezoneChange = (tzId) => {
    setSelectedTimezone(tzId);
    localStorage.setItem('selectedTimezone', tzId);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [eventsRes, settingsRes] = await Promise.all([
          axios.get(`${API}/events`),
          axios.get(`${API}/settings`)
        ]);
        setEvents(eventsRes.data);
        setSettings(settingsRes.data);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Calculate next event date (start date)
  const getNextEventDate = (event) => {
    const now = new Date();
    const currentYear = now.getFullYear();
    
    // If event has a specific year set, use that year
    if (event.year) {
      return new Date(event.year, event.month - 1, event.day);
    }
    
    // For recurring events (no year set), check if event is currently active this year
    const thisYearStart = new Date(currentYear, event.month - 1, event.day);
    const thisYearEnd = getRecurringEventEndDate(event, currentYear);
    
    // If we're currently within the event window, return this year's start
    if (thisYearEnd && now <= thisYearEnd) {
      return thisYearStart;
    }
    
    // If event has completely passed this year, use next year
    if (thisYearStart < now && (!thisYearEnd || now > thisYearEnd)) {
      return new Date(currentYear + 1, event.month - 1, event.day);
    }
    
    return thisYearStart;
  };

  // Helper: Calculate end date for a recurring event in a specific year
  const getRecurringEventEndDate = (event, year) => {
    if (!event.end_day && !event.end_month) {
      // Single day event - end of that day
      return new Date(year, event.month - 1, event.day, 23, 59, 59);
    }
    
    const endMonth = event.end_month || event.month;
    const endDay = event.end_day || event.day;
    let endYear = year;
    
    // If end month is before start month (spans year boundary), use next year for end
    if (endMonth < event.month) {
      endYear = year + 1;
    }
    
    return new Date(endYear, endMonth - 1, endDay, 23, 59, 59);
  };

  // Calculate event end date
  const getEventEndDate = (event) => {
    const startDate = getNextEventDate(event);
    const startYear = startDate.getFullYear();
    
    // If no end date specified, return end of start day
    if (!event.end_day && !event.end_month) {
      return new Date(startYear, event.month - 1, event.day, 23, 59, 59);
    }
    
    const endMonth = event.end_month || event.month;
    const endDay = event.end_day || event.day;
    let endYear = event.end_year || event.year || startYear;
    
    // If end month is before start month (spans year boundary), use next year
    if (!event.end_year && !event.year && endMonth < event.month) {
      endYear = startYear + 1;
    }
    
    return new Date(endYear, endMonth - 1, endDay, 23, 59, 59);
  };

  // Check if event is currently active (between start and end date)
  const isEventActive = (event) => {
    const now = new Date();
    const currentYear = now.getFullYear();
    
    // For events with specific year
    if (event.year) {
      const startDate = new Date(event.year, event.month - 1, event.day, 0, 0, 0);
      const endDate = getEventEndDate(event);
      return now >= startDate && now <= endDate;
    }
    
    // For recurring events, check this year's occurrence
    const thisYearStart = new Date(currentYear, event.month - 1, event.day, 0, 0, 0);
    const thisYearEnd = getRecurringEventEndDate(event, currentYear);
    
    return now >= thisYearStart && now <= thisYearEnd;
  };

  // Find currently active event
  const activeEvent = useMemo(() => {
    return events.find(event => isEventActive(event));
  }, [events]);

  // Sort events by next occurrence
  const sortedEvents = useMemo(() => {
    if (!events.length) return [];
    
    return [...events].sort((a, b) => {
      const dateA = getNextEventDate(a);
      const dateB = getNextEventDate(b);
      return dateA - dateB;
    });
  }, [events]);

  // Get the event to show in hero: active event OR next upcoming event
  const heroEvent = activeEvent || sortedEvents[0];
  const isHeroEventActive = activeEvent !== undefined && activeEvent !== null;

  // Get all upcoming events (excluding the hero event if it's active)
  const upcomingEvents = useMemo(() => {
    if (sortedEvents.length <= 1) return [];
    // If there's an active event, show all sorted events except the active one
    if (activeEvent) {
      return sortedEvents.filter(e => e.id !== activeEvent.id);
    }
    return sortedEvents.slice(1); // Skip the first (hero) event
  }, [sortedEvents, activeEvent]);

  // Group upcoming events by their next occurrence month/year
  const eventsByMonthYear = useMemo(() => {
    const grouped = {};
    upcomingEvents.forEach(event => {
      const nextDate = getNextEventDate(event);
      const key = `${nextDate.getFullYear()}-${String(nextDate.getMonth() + 1).padStart(2, '0')}`;
      if (!grouped[key]) {
        grouped[key] = {
          month: nextDate.getMonth() + 1,
          year: nextDate.getFullYear(),
          events: []
        };
      }
      grouped[key].events.push({ ...event, nextDate });
    });
    return grouped;
  }, [upcomingEvents]);

  // Get ordered month keys
  const orderedMonthKeys = useMemo(() => {
    return Object.keys(eventsByMonthYear).sort();
  }, [eventsByMonthYear]);

  if (loading) {
    return (
      <div className="min-h-screen bg-void flex items-center justify-center">
        <div className="text-cyan-electric font-mono text-xl animate-pulse">
          {t('initializingSystems')}
        </div>
      </div>
    );
  }

  // Background style based on settings
  const backgroundStyle = settings?.background_image 
    ? { 
        backgroundImage: `url(${settings.background_image})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed'
      }
    : {};

  return (
    <div className="min-h-screen bg-void hero-bg grid-bg" style={backgroundStyle}>
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass-panel border-b border-cyan-electric/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 border-2 border-cyan-electric rounded-sm flex items-center justify-center">
                <span className="text-cyan-electric font-rajdhani font-bold text-sm">SC</span>
              </div>
              <Link 
              to="/" 
              className="font-rajdhani text-xl font-bold uppercase tracking-widest text-white"
            >
              {settings?.site_title || 'Star Citizen Events'}
            </Link>
          </div>
          <div className="flex items-center gap-3">
            {/* Add to Favorites Button */}
            <button
              onClick={() => {
                // Browser bookmark functionality
                if (window.sidebar && window.sidebar.addPanel) {
                  // Firefox <23
                  window.sidebar.addPanel(document.title, window.location.href, '');
                } else if (window.external && ('AddFavorite' in window.external)) {
                  // IE Favorites
                  window.external.AddFavorite(window.location.href, document.title);
                } else {
                  // Other browsers - show instruction
                  const key = navigator.userAgent.toLowerCase().includes('mac') ? 'Cmd' : 'Ctrl';
                  alert(language === 'de' 
                    ? `Drücke ${key}+D um diese Seite als Lesezeichen hinzuzufügen`
                    : `Press ${key}+D to bookmark this page`
                  );
                }
              }}
              className="flex items-center gap-2 px-3 py-1.5 rounded border border-cyan-electric/30 bg-cyan-electric/5 text-cyan-electric hover:bg-cyan-electric/10 transition-colors"
              title={language === 'de' ? 'Als Lesezeichen hinzufügen' : 'Add to Bookmarks'}
              data-testid="bookmark-btn"
            >
              <Star className="w-4 h-4" />
            </button>

            {/* Language Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-2 px-3 py-1.5 rounded border border-gold-elite/30 bg-gold-elite/5 text-gold-elite hover:bg-gold-elite/10 transition-colors" data-testid="language-selector">
                <Languages className="w-4 h-4" />
                <span className="font-mono text-sm">{currentLanguage.flag}</span>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="glass-panel border-gold-elite/30">
                {LANGUAGES.map(lang => (
                  <DropdownMenuItem 
                    key={lang.id}
                    onClick={() => setLanguage(lang.id)}
                    className={`font-mono text-sm cursor-pointer ${language === lang.id ? 'text-gold-elite bg-gold-elite/10' : 'text-slate-300'}`}
                    data-testid={`language-${lang.id}`}
                  >
                    {lang.flag} {lang.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Timezone Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-2 px-3 py-1.5 rounded border border-cyan-electric/30 bg-cyan-electric/5 text-cyan-electric hover:bg-cyan-electric/10 transition-colors" data-testid="timezone-selector">
                <Globe className="w-4 h-4" />
                <span className="font-mono text-sm">{currentTimezone.id}</span>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="glass-panel border-cyan-electric/30">
                {TIMEZONES.map(tz => (
                  <DropdownMenuItem 
                    key={tz.id}
                    onClick={() => handleTimezoneChange(tz.id)}
                    className={`font-mono text-sm cursor-pointer ${selectedTimezone === tz.id ? 'text-cyan-electric bg-cyan-electric/10' : 'text-slate-300'}`}
                    data-testid={`timezone-${tz.id}`}
                  >
                    {tz.label} (UTC{tz.offset >= 0 ? '+' : ''}{tz.offset})
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            
            <Link 
              to="/admin" 
              className="p-2 text-slate-400 hover:text-cyan-electric transition-colors"
              data-testid="admin-link"
            >
              <Settings className="w-5 h-5" />
            </Link>
          </div>
          </div>
        </div>
      </nav>

      {/* Hero Section - Active Event AND/OR Next Event Countdown */}
      {heroEvent && (
        <section className="pt-24 pb-16 px-4 sm:px-6 lg:px-8" data-testid="hero-section">
          <div className="max-w-4xl mx-auto">
            {/* Main Site Slogan */}
            <h1 className="font-rajdhani text-2xl sm:text-3xl font-bold tracking-[0.15em] text-white mb-8 text-center">
              {language === 'de' 
                ? (settings?.hero_subtitle_de || settings?.hero_subtitle || 'Star Citizen Event Countdown')
                : (settings?.hero_subtitle_en || settings?.hero_subtitle || 'Star Citizen Event Countdown')
              }
            </h1>
            
            {/* Stacked layout: Active Event on TOP, Next Event BELOW */}
            <div className="flex flex-col gap-8">
              
              {/* Active Event Card - TOP */}
              {isHeroEventActive && (
                <div className="text-center p-6 rounded-lg border border-green-500/30 bg-green-500/5 backdrop-blur">
                  <p className="font-rajdhani text-xs uppercase tracking-[0.3em] text-green-400 mb-4">
                    {language === 'de' ? 'AKTUELLES EVENT' : 'CURRENT EVENT'}
                  </p>
                  <h2 
                    className="font-rajdhani text-3xl sm:text-4xl lg:text-5xl font-bold uppercase tracking-wider mb-4 neon-cyan"
                    style={{ color: heroEvent.color_primary }}
                    data-testid="active-event-name"
                  >
                    {heroEvent.name}
                  </h2>
                  
                  <p className="font-barlow text-slate-400 max-w-3xl mx-auto mb-6 text-base leading-relaxed">
                    {language === 'de' ? (heroEvent.description_de || heroEvent.description_en || heroEvent.description) : (heroEvent.description_en || heroEvent.description)}
                  </p>
                  
                  <div className="mb-4">
                    <span 
                      className="inline-block px-4 py-2 rounded-full text-sm font-rajdhani uppercase tracking-wider animate-pulse"
                      style={{ 
                        backgroundColor: `${heroEvent.color_primary}20`,
                        color: heroEvent.color_primary,
                        border: `2px solid ${heroEvent.color_primary}`
                      }}
                    >
                      {language === 'de' ? '🎮 LÄUFT JETZT!' : '🎮 LIVE NOW!'}
                    </span>
                  </div>
                  
                  <p className="font-mono text-xs text-slate-500 mb-3">
                    {language === 'de' ? 'Endet in:' : 'Ends in:'}
                  </p>
                  
                  <CountdownTimer 
                    targetDate={getEventEndDate(heroEvent)} 
                    eventColor={heroEvent.color_primary}
                    timezoneOffset={currentTimezone.offset}
                    language={language}
                  />
                  
                  <p className="mt-4 font-mono text-sm text-slate-500">
                    {heroEvent.day}. {months[heroEvent.month - 1]} - {heroEvent.end_day || heroEvent.day}. {months[(heroEvent.end_month || heroEvent.month) - 1]} {heroEvent.year || getNextEventDate(heroEvent).getFullYear()}
                    <span className="ml-2 text-cyan-electric/50">({currentTimezone.id})</span>
                  </p>
                </div>
              )}
              
              {/* Next Upcoming Event Card - BELOW (or full display if no active event) */}
              {(() => {
                const nextUpcoming = isHeroEventActive 
                  ? sortedEvents.find(e => e.id !== activeEvent?.id)
                  : heroEvent;
                
                if (!nextUpcoming) return null;
                
                return (
                  <div className={`text-center ${isHeroEventActive ? 'p-6 rounded-lg border border-cyan-electric/20 bg-void/50 backdrop-blur' : ''}`}>
                    <p className="font-rajdhani text-xs uppercase tracking-[0.3em] text-cyan-electric/70 mb-4">
                      {t('nextEvent')}
                    </p>
                    <h2 
                      className="font-rajdhani text-3xl sm:text-4xl lg:text-5xl font-bold uppercase tracking-wider mb-4 neon-cyan"
                      style={{ color: nextUpcoming.color_primary }}
                      data-testid="next-event-name"
                    >
                      {nextUpcoming.name}
                    </h2>
                    
                    <p className="font-barlow text-slate-400 max-w-3xl mx-auto mb-6 text-base leading-relaxed">
                      {language === 'de' ? (nextUpcoming.description_de || nextUpcoming.description_en || nextUpcoming.description) : (nextUpcoming.description_en || nextUpcoming.description)}
                    </p>
                    
                    <p className="font-mono text-xs text-slate-500 mb-3">
                      {language === 'de' ? 'Startet in:' : 'Starts in:'}
                    </p>
                    
                    <CountdownTimer 
                      targetDate={getNextEventDate(nextUpcoming)} 
                      eventColor={nextUpcoming.color_primary}
                      timezoneOffset={currentTimezone.offset}
                      language={language}
                    />
                    
                    <p className="mt-4 font-mono text-sm text-slate-500">
                      {nextUpcoming.day}. {months[nextUpcoming.month - 1]} {nextUpcoming.year || getNextEventDate(nextUpcoming).getFullYear()}
                      {(nextUpcoming.end_day || nextUpcoming.end_month) && (
                        <>
                          {' - '}
                          {nextUpcoming.end_day || nextUpcoming.day}. {months[(nextUpcoming.end_month || nextUpcoming.month) - 1]}
                          {nextUpcoming.end_year ? ` ${nextUpcoming.end_year}` : (nextUpcoming.year ? ` ${nextUpcoming.year}` : ` ${getNextEventDate(nextUpcoming).getFullYear()}`)}
                        </>
                      )}
                      <span className="ml-2 text-cyan-electric/50">({currentTimezone.id})</span>
                    </p>
                  </div>
                );
              })()}
            </div>
          </div>
        </section>
      )}

      {/* Events List by Month/Year - Chronologically */}
      <section className="pb-24 px-4 sm:px-6 lg:px-8" data-testid="events-section">
        <div className="max-w-6xl mx-auto">
          {orderedMonthKeys.map(key => {
            const { month, year, events } = eventsByMonthYear[key];
            const currentYear = new Date().getFullYear();
            const showYear = year !== currentYear;
            
            return (
              <div key={key} className="mb-12">
                {/* Month Header */}
                <div className="month-divider mb-6">
                  <h3 className="font-rajdhani text-lg uppercase tracking-[0.2em] text-cyan-electric/70 bg-void px-4 inline-block relative z-10">
                    {months[month - 1]} {showYear && year}
                  </h3>
                </div>
                
                {/* Events Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {events.map(event => (
                    <EventCard 
                      key={event.id} 
                      event={event} 
                      nextEventDate={event.nextDate}
                      timezoneOffset={currentTimezone.offset}
                      timezoneId={currentTimezone.id}
                      language={language}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Footer */}
      <footer className="glass-panel border-t border-cyan-electric/20 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            {/* Comments Link */}
            <Link 
              to="/comments"
              className="flex items-center gap-2 text-cyan-electric hover:text-cyan-electric/80 font-rajdhani uppercase tracking-wider text-sm transition-colors"
              data-testid="comments-link"
            >
              <MessageSquare className="w-4 h-4" />
              {language === 'de' ? 'Kommentare' : 'Comments'}
            </Link>
            
            {/* Footer Text */}
            <p className="font-mono text-xs text-slate-500 hidden sm:block">
              {language === 'de'
                ? (settings?.footer_text_de || settings?.footer_text || '© 2955 Vereintes Imperium der Erde')
                : (settings?.footer_text_en || settings?.footer_text || '© 2955 United Empire of Earth')
              }
            </p>
            
            {/* Vote Box */}
            <VoteBox language={language} />
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
